import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, merge, of, switchMap, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';

import { ImpersonationService } from '../../services/impersonation/impersonation.service';
import { SecureAuthStorageService } from '../../services/secure-auth-storage/secure-auth-storage.service';
import { ImpersonationActions } from './impersonation.actions';

@Injectable()
export class ImpersonationEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(ImpersonationService);
  private readonly _storage = inject(SecureAuthStorageService);

  public readonly connect$ = createEffect(() =>
    merge(
      this._actions$.pipe(
        ofType(ImpersonationActions.switchBuyer),
        map(({ id, name }) => ImpersonationActions.connect({ id, name })),
      ),
      this._actions$.pipe(
        ofType(ImpersonationActions.connect),
        switchMap(({ id, name }) =>
          this._service.getBuyerToken(id).pipe(
            map((token) =>
              ImpersonationActions.connectSuccess({
                buyerCompany: { id, name, token },
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  ImpersonationActions.connectFailure({ error: error.message }),
                ),
              { notify: true },
            ),
          ),
        ),
      ),
    ),
  );

  public readonly disconnect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ImpersonationActions.disconnect),
      map(() => ImpersonationActions.disconnectSuccess()),
    ),
  );

  public readonly saveOnConnect$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ImpersonationActions.connectSuccess),
        concatMap(({ buyerCompany }) =>
          this._storage.saveImpersonatedBuyer(buyerCompany),
        ),
      ),
    { dispatch: false },
  );

  public readonly clearImpersonationStorage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          ImpersonationActions.connectFailure,
          ImpersonationActions.disconnectSuccess,
        ),
        tap(() => this._storage.clearImpersonation()),
      ),
    { dispatch: false },
  );
}
